import { createStore } from 'owa-satcheljs';
import { getIndexerValueForMailboxInfo } from 'owa-client-types';
import type { MailboxInfo } from 'owa-client-types';
import { getCopilotCatchUpOptionStore } from '../utils/copilotCatchUpStorageUtils';
import { logUsage } from 'owa-analytics';
import { setItem, itemExists, type LocalStorageKeys } from 'owa-local-storage';
import type { ExtractedTaskDetails } from '../types/ExtractedTaskDetails';

export type CatchUpTimeFrame = '3days' | '2days' | '24hours';

export interface CopilotCatchUpOptionStore {
    id: string;
    isEnabled: boolean;
    shouldUsePrompt: boolean;
    shouldApplyPromptToIncomingMessages: boolean;
    includeAllEmail: boolean;
    timeFrame: CatchUpTimeFrame;
    needsReplyItemIds: string[];
    needsReplyConversationIds: string[];
    needsActionItemIds: string[];
    needsActionConversationIds: string[];
    needsScheduleItemIds: string[];
    needsScheduleConversationIds: string[];
    extractedTaskDetails: {
        [key: string]: ExtractedTaskDetails;
    };
    ocsTopics: string;
    topicsBizchatPrompt: string;
    forceResetItems: boolean;
    useNaturalLanguageSummary: boolean;
}

interface CopilotCatchUpOptionStoreMap {
    entries: Map<string, CopilotCatchUpOptionStore>;
}

export const getCopilotCatchUpStoreMap: () => CopilotCatchUpOptionStoreMap =
    createStore<CopilotCatchUpOptionStoreMap>('CopilotCatchUpOptionStore', {
        entries: new Map<string, CopilotCatchUpOptionStore>(),
    });

export default function getStore(mailboxInfo: MailboxInfo): CopilotCatchUpOptionStore {
    const copilotCatchUpStoreMap = getCopilotCatchUpStoreMap();
    const name = getIndexerValueForMailboxInfo(mailboxInfo) + '_CopilotCatchUpOptionStore';
    let cachedStore = copilotCatchUpStoreMap.entries.get(name);

    if (!cachedStore) {
        copilotCatchUpStoreMap.entries.set(name, getCopilotCatchUpOptionStore(mailboxInfo));
        cachedStore = copilotCatchUpStoreMap.entries.get(name);
    }

    if (!cachedStore) {
        throw new Error('Entry does not exist in the owa-copilot-catchup-option store');
    }

    // We need to log once for users that already have the setting enabled
    // For new users, this will be logged as false.
    // For every user, once the setting is changed, we will log the new value.
    // This is temporary to measure the impact of the setting in Experimental API
    const localStorageKey: LocalStorageKeys = `mail_copilot_catchup_apply_prompt_incomming_messages_${name}`;
    if (!itemExists(window, localStorageKey)) {
        logUsage('CopilotCatchUpApplyPromptToIncommingMessages', {
            shouldApplyPromptToIncomingMessages: cachedStore.shouldApplyPromptToIncomingMessages,
        });
        setItem(window, localStorageKey, JSON.stringify({ logged: true }));
    }

    return cachedStore;
}
